import { action, makeObservable, onBecomeObserved } from "mobx"

import {
  CRYPTO_PAYOUTS_MOCK_KEY,
  getCryptoPayoutsMock,
} from "@src/app/mocks/cryptoPayoutsMock"
import { TransactionsOutFilters } from "@src/pages/transactionsOut/constants"
import { catchErrors, payoutsApi } from "@src/shared/api"
import {
  NetworkCrypto,
  Pagination,
  PayoutResponseStatusEnum,
  SymbolCrypto,
} from "@src/shared/api/generated"
import { registerCollectionMock } from "@src/shared/classes/Collection/collectionMocks"
import { CollectionWithPagination } from "@src/shared/classes/Collection/CollectionWithPagination"
import { loading } from "@src/shared/classes/utils/loading"
import { CollectionFilters } from "@src/shared/componemts/TableFilter/classes/CollectionFilters"
import { CryptoPayout } from "../types"

registerCollectionMock(CRYPTO_PAYOUTS_MOCK_KEY, getCryptoPayoutsMock)

export type CryptoPayoutsLoadRequest = {
  address?: string
  amlLevel?: "high" | "low" | "medium"
  date?: {
    dateFrom?: string
    dateTo?: string
  }
  extTxId?: string
  externalId?: string
  massPayoutId?: string
  network?: NetworkCrypto
  order?: "asc" | "desc"
  page?: number
  status?: PayoutResponseStatusEnum
  symbol?: SymbolCrypto
  type?: "payout" | "withdrawal"
}

export type CryptoPayoutsFilters = Omit<CryptoPayoutsLoadRequest, "page">

export class CryptoPayoutsCollection extends loading(
  ["fetch"],
  CollectionWithPagination<CryptoPayout>,
) {
  filter = new CollectionFilters<CryptoPayoutsFilters>(
    TransactionsOutFilters,
    this.load,
  )

  protected mockKey = CRYPTO_PAYOUTS_MOCK_KEY

  constructor() {
    super()

    makeObservable(this, {
      fetch: action.bound,
    })

    onBecomeObserved(this, "isLoading", this.load)
  }

  async fetch(req: CryptoPayoutsLoadRequest = {}, signal?: AbortSignal) {
    try {
      const { page = 0 } = req

      const {
        data: { data },
      } = await payoutsApi.listCryptoPayouts(
        req.order,
        this.calcOffset(page),
        this.pageSize,
        req.externalId,
        req.date?.dateFrom,
        req.date?.dateTo,
        req.massPayoutId,
        req.type,
        req.extTxId,
        req.status,
        req.amlLevel,
        req.network,
        req.symbol,
        req.address,
        { signal },
      )

      if (data?.data && data.pagination) {
        return {
          data: data.data as CryptoPayout[],
          pagination: data.pagination as Pagination,
        }
      }
    } catch (e: any) {
      catchErrors(e)
    }

    return null
  }
}
